import axios from 'axios';
import { API_URL } from 'src/env.json';
import { AuthUtils } from 'src/services/Auth/Auth.utils';

export interface MediaObject {
  id: string;
  file_name: string;
  name?: string;
  mime_type: string;
  size: number;
  url?: string;
  index_url?: string;
  created_at: string;
  updated_at: string;
  directory_id?: string | null;
  tags?: string[];
  metadata?: any;
  generated_conversions?: {
    thumbnail?: string;
    poster?: string;
  };
  video?: {
    thumbnail_url?: string;
  };
  custom_properties?: {
    originalUrl?: string;
  };
  full_url?: string;
  preview_url?: string;
}

export interface MediaDirectory {
  id: string;
  name: string;
  path?: string;
  parent_id?: string | null;
  parent_directory_id?: string | null;
  created_at?: string;
  updated_at?: string;
  custom_properties?: {
    apiPath?: string;
    path?: string[];
    fullPath?: string[];
    isRootLevel?: boolean;
  };
}

export interface MediaResponse {
  files: MediaObject[];
  folders: MediaDirectory[];
  currentPath?: any[];
}

export interface StorageUsageStatus {
  used_storage: number;
  max_storage: number;
  percentage_used: number;
  fileCount?: number;
}

interface StorageStatusResponse {
  global?: {
    sizeUsed: number;
    sizeLimit: number;
    sizeRemaining: number;
    fileCount: number;
  };
  sizeUsed: number;
  sizeLimit: number;
  sizeRemaining: number;
  fileCount: number;
}

/**
 * Admin Media Service - mirrors customer frontend's MediaService approach
 * Fetches brand media using AI Agent API with proper recursive directory handling
 */
export class AdminMediaService {
  private baseUrl: string;

  constructor() {
    // Use environment API URL or fallback to localhost
    this.baseUrl = API_URL || 'http://localhost/api';
  }

  /**
   * Get auth headers for API calls
   */
  private getHeaders(brandId?: string): Record<string, string> {
    // Get authentication token from AuthUtils (admin frontend pattern)
    const jwt = AuthUtils.getJwt();
    const token = jwt?.token;
    
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Authorization': token ? `Bearer ${token}` : '',
    };

    // Add x-brand-id header if brandId is provided (matches customer frontend pattern)
    if (brandId) {
      headers['x-brand-id'] = brandId;
    }

    return headers;
  }

  /**
   * Get AI Agent ID - matches customer frontend's approach
   */
  private async getAgentId(): Promise<string> {
    try {
      // Fix the base URL to ensure proper API path construction
      let baseUrl = this.baseUrl;
      // Remove any trailing slashes
      if (baseUrl.endsWith('/')) {
        baseUrl = baseUrl.slice(0, -1);
      }

      // Ensure we have /api in the path
      let endpoint;
      if (baseUrl.endsWith('/api')) {
        // If URL ends with /api, use it directly
        endpoint = `${baseUrl}/ai-agent`;
      } else {
        // Otherwise ensure we add /api
        endpoint = `${baseUrl}/api/ai-agent`;
      }

      const response = await axios.get(endpoint, {
        headers: this.getHeaders(),
      });

      // Handle various response formats (matching customer frontend)
      let agentId: string | null = null;

      if (response.data && response.data.id) {
        // Standard format: { id: "agent-id" }
        agentId = response.data.id;
      } else if (response.data && response.data.agent && response.data.agent.id) {
        // Alternative format: { agent: { id: "agent-id" } }
        agentId = response.data.agent.id;
      } else if (response.data && response.data.data && response.data.data.id) {
        // Wrapper format: { data: { id: "agent-id" } }
        agentId = response.data.data.id;
      } else {
        console.error('Unexpected response format from agent API:', response.data);
        throw new Error('No valid agent ID found in response');
      }

      if (!agentId) {
        throw new Error('No agent ID returned');
      }

      return agentId;
    } catch (error) {
      console.error('Error getting agent ID:', error);
      throw new Error('Agent ID not found');
    }
  }

  /**
   * Get media items from AI Agent API with brand filtering
   */
  private async getMediaItems(
    directoryId?: string | null,
    forceRefresh = false,
    fullPath?: string,
    mimeType?: string,
    searchQuery?: string,
    brandId?: string
  ): Promise<MediaResponse> {
    const agentId = await this.getAgentId();
    
    const params: Record<string, string> = {
      agent_id: agentId,
      sortBy: 'created_at',
      sortOrder: 'desc',
    };

    // Add path if directoryId is provided
    if (directoryId) {
      params.path = directoryId;
    }

    // Add brand filtering
    if (brandId) {
      params.user_id = brandId;
    }

    // Add mime type filter
    if (mimeType && mimeType !== 'all') {
      params.mime_type = mimeType;
    }

    // Add search query
    if (searchQuery && searchQuery.trim()) {
      params.query = searchQuery.trim();
    }

    try {
      const response = await axios.get(`${this.baseUrl}/ai-agent/media`, {
        headers: this.getHeaders(brandId),
        params,
      });

      // Map response to expected format
      const data = response.data || { files: [], folders: [] };
      
      return {
        files: (data.files || []).map((file: any) => ({
          id: file.id || '',
          file_name: file.name || file.file_name || 'Unknown file',
          name: file.name || file.file_name || 'Unknown file',
          mime_type: file.mime_type || this.getMimeTypeFromName(file.name || file.file_name || ''),
          size: file.size || 0,
          url: file.url || '',
          index_url: file.index_url || file.url || '',
          created_at: file.created_at || new Date().toISOString(),
          updated_at: file.updated_at || file.created_at || new Date().toISOString(),
          directory_id: directoryId,
          tags: file.tags || [],
          metadata: file.metadata || {},
          generated_conversions: file.generated_conversions || {},
          video: file.video || {},
          custom_properties: file.custom_properties || {},
          full_url: file.full_url || file.url || '',
          preview_url: file.preview_url || file.url || '',
        })),
        folders: data.folders || [],
        currentPath: data.currentPath || [],
      };
    } catch (error) {
      console.error('Error fetching media items:', error);
      return { files: [], folders: [] };
    }
  }

  /**
   * Get all directories using the dedicated /directories endpoint
   * This mirrors customer-fe's getDirectoryTree pattern - single API call instead of recursive fetching
   */
  async getDirectoriesRecursive(brandId?: string): Promise<MediaDirectory[]> {
    try {
      const agentId = await this.getAgentId();

      // Use the dedicated directories endpoint - returns full tree in one request
      const response = await axios.get(`${this.baseUrl}/ai-agent/media/directories`, {
        headers: this.getHeaders(brandId),
        params: {
          agent_id: agentId,
          ...(brandId ? { user_id: brandId } : {}),
        },
      });

      const directoryTree = response.data?.directories || response.data || [];

      // Flatten the hierarchical tree into MediaDirectory array
      const allDirectories: MediaDirectory[] = [];

      const processDirectory = (
        dir: any,
        parentId: string | null,
        pathSegments: string[],
      ): void => {
        const currentPath = [...pathSegments, dir.name];
        const isRoot = parentId === null;
        const directoryId = isRoot ? `root:${dir.name}` : `subdir:${currentPath.join('/')}`;

        const directoryObj: MediaDirectory = {
          id: directoryId,
          name: dir.name,
          parent_id: parentId,
          parent_directory_id: parentId,
          created_at: dir.created_at || new Date().toISOString(),
          updated_at: dir.updated_at || new Date().toISOString(),
          custom_properties: {
            apiPath: currentPath.join('/'),
            path: currentPath,
            fullPath: currentPath,
            isRootLevel: isRoot,
          },
        };

        allDirectories.push(directoryObj);

        // Process children recursively (in-memory, no API calls)
        if (Array.isArray(dir.children) && dir.children.length > 0) {
          for (const child of dir.children) {
            processDirectory(child, directoryId, currentPath);
          }
        }
      };

      // Process all root directories
      if (Array.isArray(directoryTree)) {
        for (const rootDir of directoryTree) {
          processDirectory(rootDir, null, []);
        }
      }

      return allDirectories;
    } catch (error) {
      console.error('Error fetching directories:', error);
      return [];
    }
  }

  /**
   * Get directories from AI Agent API (legacy method - uses /directories endpoint)
   */
  private async getDirectories(forceRefresh = false, brandId?: string): Promise<MediaDirectory[]> {
    try {
      const agentId = await this.getAgentId();
      
      const params: Record<string, string> = {
        agent_id: agentId,
      };

      if (brandId) {
        params.user_id = brandId;
      }

      const response = await axios.get(`${this.baseUrl}/ai-agent/media/directories`, {
        headers: this.getHeaders(brandId),
        params,
      });

      return (response.data?.directories || []).map((dir: any) => ({
        id: dir.id || '',
        name: dir.name || 'Unknown Directory',
        path: dir.path || '',
        parent_id: dir.parent_id || null,
      }));
    } catch (error) {
      console.error('Error fetching directories:', error);
      return [];
    }
  }

  /**
   * Get media items for a specific directory path
   * Public method for use by components
   */
  async getMediaForDirectory(
    directoryPath: string | null,
    mimeType?: string,
    searchQuery?: string,
    brandId?: string,
  ): Promise<MediaResponse> {
    return this.getMediaItems(
      directoryPath,
      false,
      undefined,
      mimeType,
      searchQuery,
      brandId,
    );
  }

  /**
   * Fetch brand-specific media files recursively from all directories
   * This mirrors the customer frontend's fetchBrandMedia function
   */
  async getBrandMedia(brandId: string): Promise<MediaObject[]> {
    if (!brandId) {
      return [];
    }

    try {
      const allFiles: MediaObject[] = [];

      // First, get files from root directory (same as customer frontend)
      const rootResponse = await this.getMediaItems(
        null, // directoryId - use null for root
        true, // forceRefresh
        undefined, // fullPath
        undefined, // mimeType
        undefined, // searchQuery - don't use wildcard, get actual files
        brandId, // brandId for filtering
      );

      // Add root files
      if (rootResponse.files) {
        allFiles.push(...rootResponse.files);
      }

      // Get all directories to fetch from recursively (use recursive method to get nested directories)
      const directories = await this.getDirectoriesRecursive(brandId);

      // Fetch media from each directory
      for (const directory of directories) {
        try {
          // Clean the directory ID to remove prefixes like 'root:' or 'subdir:'
          let cleanDirectoryId = directory.id;
          if (cleanDirectoryId.startsWith('root:')) {
            cleanDirectoryId = cleanDirectoryId.substring('root:'.length);
          } else if (cleanDirectoryId.startsWith('subdir:')) {
            cleanDirectoryId = cleanDirectoryId.substring('subdir:'.length);
          }

          const directoryResponse = await this.getMediaItems(
            cleanDirectoryId,
            false, // don't force refresh for subdirectories
            undefined, // fullPath
            undefined, // mimeType
            undefined, // searchQuery
            brandId, // brandId for filtering
          );

          if (directoryResponse.files) {
            allFiles.push(...directoryResponse.files);
          }
        } catch (dirError) {
          console.warn(`Failed to fetch media from directory ${directory.name}:`, dirError);
          // Continue with other directories even if one fails
        }
      }

      // Remove duplicates based on file ID
      const uniqueFiles = allFiles.filter(
        (file, index, self) => index === self.findIndex((f) => f.id === file.id),
      );

      return uniqueFiles;
    } catch (error) {
      console.error('Error fetching brand media:', error);
      return [];
    }
  }

  /**
   * Get storage usage status including file count
   * This is more efficient than fetching all media recursively
   * @param brandId - Optional brand ID for filtering
   * @returns Storage usage statistics
   */
  async getStorageUsageStatus(brandId?: string): Promise<StorageUsageStatus> {
    try {
      const response = await axios.get(`${this.baseUrl}/ai-agent/media/storage/status`, {
        headers: this.getHeaders(brandId),
      });

      let responseData: StorageStatusResponse;
      if (response.data && response.data.data) {
        responseData = response.data.data;
      } else {
        responseData = response.data;
      }

      const globalData = responseData.global || responseData;
      const usedStorage = globalData.sizeUsed;
      const maxStorage = globalData.sizeLimit;
      const percentageUsed = maxStorage > 0 ? (usedStorage / maxStorage) * 100 : 0;

      return {
        used_storage: usedStorage,
        max_storage: maxStorage,
        percentage_used: percentageUsed,
        fileCount: globalData.fileCount,
      };
    } catch (error) {
      console.error('Error fetching storage usage status:', error);
      return {
        used_storage: 0,
        max_storage: 0,
        percentage_used: 0,
        fileCount: 0,
      };
    }
  }

  /**
   * Helper method to determine MIME type from filename
   * Exported for use in other components
   * @param fileName - The filename to analyze
   * @returns MIME type string
   */
  getMimeTypeFromName(fileName: string): string {
    const extension = fileName.split('.').pop()?.toLowerCase();

    const mimeTypes: { [key: string]: string } = {
      jpg: 'image/jpeg',
      jpeg: 'image/jpeg',
      png: 'image/png',
      gif: 'image/gif',
      svg: 'image/svg+xml',
      webp: 'image/webp',
      mp4: 'video/mp4',
      webm: 'video/webm',
      mov: 'video/quicktime',
      avi: 'video/x-msvideo',
      mp3: 'audio/mpeg',
      wav: 'audio/wav',
      ogg: 'audio/ogg',
      m4a: 'audio/mp4',
      pdf: 'application/pdf',
      doc: 'application/msword',
      docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      xls: 'application/vnd.ms-excel',
      xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    };

    return extension && mimeTypes[extension] ? mimeTypes[extension] : 'application/octet-stream';
  }

  /**
   * Get appropriate URL for displaying media
   * @param media - MediaObject to get URL for
   * @returns Display URL string
   */
  getMediaUrl(media: MediaObject): string {
    // First check for direct URL from API
    if (media.url) return media.url;
    if (media.custom_properties?.originalUrl) return media.custom_properties.originalUrl;

    // Standard properties for media library items
    if (media.index_url) return media.index_url;
    if (media.video?.thumbnail_url) return media.video.thumbnail_url;
    if (media.generated_conversions?.thumbnail) return media.generated_conversions.thumbnail;

    // Check for any other URL-like properties
    if (media.full_url) return media.full_url;
    if (media.preview_url) return media.preview_url;

    return '';
  }

  /**
   * Get thumbnail URL for videos
   * @param media - MediaObject to get thumbnail for
   * @returns Thumbnail URL string
   */
  getThumbnailUrl(media: MediaObject): string {
    // Only return actual thumbnail/poster images, NOT the video file itself
    if (
      media.generated_conversions?.thumbnail &&
      !media.generated_conversions.thumbnail.match(/\.(mp4|webm|mov|avi)$/i)
    ) {
      return media.generated_conversions.thumbnail;
    }

    // Check for poster in generated_conversions (should be an image)
    if (
      media.generated_conversions?.poster &&
      !media.generated_conversions.poster.match(/\.(mp4|webm|mov|avi)$/i)
    ) {
      return media.generated_conversions.poster;
    }

    // Check for video thumbnail URL (should be an image, not the video file)
    if (media.video?.thumbnail_url && !media.video.thumbnail_url.match(/\.(mp4|webm|mov|avi)$/i)) {
      return media.video.thumbnail_url;
    }

    return '';
  }

  /**
   * Download media file by URL
   * Fetches the file as a blob and triggers a download to ensure it downloads instead of opening in new tab
   */
  async downloadMedia(mediaUrl: string, fileName?: string): Promise<void> {
    try {
      // Fetch the file as a blob
      const response = await fetch(mediaUrl);
      if (!response.ok) {
        throw new Error(`Failed to fetch file: ${response.statusText}`);
      }
      
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      
      // Create anchor and trigger download
      const anchor = document.createElement('a');
      anchor.style.display = 'none';
      anchor.href = blobUrl;
      anchor.download = fileName || 'download';
      
      document.body.append(anchor);
      anchor.click();
      
      // Delay cleanup — browser reads blob URL asynchronously after click(),
      // revoking too early causes the download to silently fail
      setTimeout(() => {
        anchor.remove();
        URL.revokeObjectURL(blobUrl);
      }, 60000);
    } catch (error) {
      console.error('Download failed:', error);
      // Fallback: open in new tab if fetch fails (e.g., CORS issues)
      window.open(mediaUrl, '_blank', 'noopener');
    }
  }

  /**
   * Download multiple media files with delay
   */
  async downloadMultiple(mediaItems: MediaObject[]): Promise<void> {
    for (let i = 0; i < mediaItems.length; i++) {
      const media = mediaItems[i];
      const url = media.url || media.index_url || media.full_url;
      
      if (url) {
        this.downloadMedia(url, media.file_name);
        // Add delay between downloads to prevent browser blocking
        await new Promise(resolve => setTimeout(resolve, 500));
      }
    }
  }

  /**
   * Download multiple media files with progress tracking and cancellation support.
   * Tries fetch+blob first for a true download. On CORS/network failure, falls back
   * to window.open() and tracks it separately so callers can provide accurate feedback.
   */
  async downloadMultipleTracked(
    mediaItems: DownloadableMedia[],
    signal?: AbortSignal,
  ): Promise<BulkDownloadResult> {
    let downloaded = 0;
    let skipped = 0;
    let failed = 0;
    let fallbackOpened = 0;

    for (let i = 0; i < mediaItems.length; i++) {
      if (signal?.aborted) break;

      const media = mediaItems[i];
      const url = media.index_url;

      if (!url) {
        skipped++;
        continue;
      }

      try {
        const response = await fetch(url, { signal });
        if (!response.ok) {
          throw new Error(`Failed to fetch file: ${response.statusText}`);
        }

        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);

        const anchor = document.createElement('a');
        anchor.style.display = 'none';
        anchor.href = blobUrl;
        anchor.download = media.file_name || 'download';

        document.body.append(anchor);
        anchor.click();

        setTimeout(() => {
          anchor.remove();
          URL.revokeObjectURL(blobUrl);
        }, 60000);

        downloaded++;
      } catch (error) {
        // Don't count aborted requests as failures
        if (signal?.aborted) break;

        // Fallback: open in new tab (e.g., CORS-restricted S3 URLs)
        try {
          window.open(url, '_blank', 'noopener');
          fallbackOpened++;
        } catch {
          console.error('File download failed:', media.file_name, error);
          failed++;
        }
      }

      // Delay between downloads to prevent browser blocking
      if (i < mediaItems.length - 1 && !signal?.aborted) {
        await new Promise(resolve => setTimeout(resolve, 500));
      }
    }

    return { downloaded, skipped, failed, fallbackOpened };
  }
}

export interface DownloadableMedia {
  index_url?: string;
  file_name: string;
}

export interface BulkDownloadResult {
  downloaded: number;
  skipped: number;
  failed: number;
  fallbackOpened: number;
}

// Singleton instance for use across components
export const adminMediaService = new AdminMediaService();

// Factory function for dependency injection in tests
export const createAdminMediaService = (): AdminMediaService => new AdminMediaService();
